'use client'

import { useLocale } from '@/components/Link'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import { useSnackbar } from '@/hooks/useSnackbar'
import { Close, StarRateRounded } from '@mui/icons-material'
import {
  Badge,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Stack,
  SxProps,
  Theme,
  Typography,
  alpha,
} from '@mui/material'

import type { Locale } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { enGB, de, it, nl } from 'date-fns/locale'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { groupItems } from './groupItems'
import RatingModal from './RatingModal'
import type { CanteenRatingItem } from './types'
import { useCanteenRatingDismiss } from './useCanteenRatingDismiss'
import { useCanteenRatingItems } from './useCanteenRatingItems'

const localeMap: Record<string, Locale> = { en: enGB, de, nl, it }

type Props = {
  userId?: string
  sx?: SxProps<Theme>
}

const CanteenRatingBanner: React.FC<Props> = ({ userId, sx }) => {
  const { t } = useTranslation()
  const locale = useLocale() || 'en-gb'
  const { showSnackbar, SnackbarComponent } = useSnackbar()

  const ratingEnabled = useFeatureFlag(SPLITS.CC_ITEM_RATING)
  const { items, refetch } = useCanteenRatingItems(ratingEnabled ? userId : undefined)
  const { isDismissed, dismissItems } = useCanteenRatingDismiss(userId)

  const [modalItemId, setModalItemId] = useState<number | null>(null)
  const [ratedIds, setRatedIds] = useState<Set<number>>(new Set())
  const [expanded, setExpanded] = useState(false)
  const [hidden, setHidden] = useState(false)
  // Latched: without it, rating one of two items swaps the layout under the pointer.
  const [wasMulti, setWasMulti] = useState(false)

  const dateLocale = localeMap[locale.split('-')[0] ?? 'en'] ?? enGB

  const visibleItems = useMemo(
    () => items.filter((item) => !isDismissed(item.id) && !ratedIds.has(item.id)),
    [items, isDismissed, ratedIds],
  )

  if (visibleItems.length > 1 && !wasMulti) setWasMulti(true)

  const groups = useMemo(() => groupItems(visibleItems), [visibleItems])
  const modalItem = visibleItems.find((item) => item.id === modalItemId) ?? null

  const formatDay = (item: CanteenRatingItem) =>
    formatInTimeZone(new Date(item.date), item.timezone, 'EEEE d MMMM', {
      locale: dateLocale,
    })

  const handleDismiss = () => {
    dismissItems(visibleItems.map((item) => item.id))
    setHidden(true)
  }

  const handleRated = (itemId: number) => {
    setRatedIds((prev) => new Set(prev).add(itemId))
    setModalItemId(null)
    showSnackbar('success', <>{t('dashboard:cateringRating.rateSuccess')}</>, {
      autoHideDuration: 4000,
    })
    void refetch()
  }

  const handleRateError = () =>
    showSnackbar('error', <>{t('dashboard:cateringRating.rateError')}</>, {
      autoHideDuration: 4000,
    })

  // Rendered above the early return so the confirmation outlives the emptied banner.
  if (!ratingEnabled || hidden || visibleItems.length === 0) {
    return <>{SnackbarComponent}</>
  }

  const single = visibleItems.length === 1 && !wasMulti
  const firstItem = visibleItems[0]

  const starTile = (
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: 2,
        bgcolor: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
      <StarRateRounded sx={{ color: 'primary.contrastText' }} />
    </Box>
  )

  return (
    <>
      {SnackbarComponent}
      <Box
        data-cy="cc-review-banner"
        sx={{
          position: 'relative',
          p: 2,
          pr: 5,
          mt: 4,
          borderRadius: 2,
          border: '1px solid',
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.18),
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
          ...(sx || {}),
        }}>
        <IconButton
          size="small"
          onClick={handleDismiss}
          aria-label={t('dashboard:cateringRating.later')}
          data-cy="cc-review-dismiss"
          sx={{ position: 'absolute', top: 6, right: 6, color: 'text.secondary' }}>
          <Close fontSize="small" />
        </IconButton>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Stack direction="row" spacing={2} alignItems="center" flexGrow={1} minWidth={0}>
            {single ? (
              starTile
            ) : (
              <Badge
                badgeContent={visibleItems.length}
                color="warning"
                overlap="circular"
                data-cy="cc-review-count">
                {starTile}
              </Badge>
            )}
            <Stack spacing={0.25} minWidth={0}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                rowGap={0.5}>
                <Typography variant="subtitle3" fontWeight={600} color="text.primary">
                  {single && firstItem
                    ? t('dashboard:canteenRating.singleTitle', {
                        item: firstItem.name,
                        vendor: firstItem.vendorName,
                      })
                    : t('dashboard:canteenRating.multiTitle', {
                        count: visibleItems.length,
                      })}
                </Typography>
                {single && firstItem && <Chip size="small" label={formatDay(firstItem)} />}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {t('dashboard:cateringRating.helperText')}
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="center" flexShrink={0}>
            <Button
              size="small"
              onClick={handleDismiss}
              sx={{ color: 'text.secondary' }}
              data-cy="cc-review-later">
              {t('dashboard:cateringRating.later')}
            </Button>
            {single && firstItem ? (
              <Button
                variant="contained"
                size="small"
                onClick={() => setModalItemId(firstItem.id)}
                data-cy="cc-review-leave-feedback">
                {t('dashboard:cateringRating.leaveFeedback')}
              </Button>
            ) : (
              <Button
                variant="contained"
                size="small"
                onClick={() => setExpanded((prev) => !prev)}
                data-cy="cc-review-toggle">
                {expanded
                  ? t('dashboard:cateringRating.collapse')
                  : t('dashboard:cateringRating.rateOrders')}
              </Button>
            )}
          </Stack>
        </Stack>

        {!single && (
          <Collapse in={expanded} unmountOnExit>
            <Stack spacing={0} mt={1.5}>
              {groups.map((group) => (
                <Box key={group.key}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ pt: 1.5, pb: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" fontWeight={700} color="text.primary">
                      {formatInTimeZone(
                        new Date(group.items[0]?.date ?? ''),
                        group.timezone,
                        'EEEE d MMMM',
                        { locale: dateLocale },
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      |
                    </Typography>
                    <Typography variant="body2" fontWeight={700} color="primary.main">
                      {group.vendorName}
                    </Typography>
                  </Stack>
                  {group.items.map((item) => (
                    <Stack
                      key={item.id}
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ py: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {item.name}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setModalItemId(item.id)}
                        data-cy="cc-review-row-rate">
                        {t('dashboard:cateringRating.rate')}
                      </Button>
                    </Stack>
                  ))}
                </Box>
              ))}
            </Stack>
          </Collapse>
        )}
      </Box>

      <RatingModal
        item={modalItem}
        onClose={() => setModalItemId(null)}
        onRated={handleRated}
        onError={handleRateError}
      />
    </>
  )
}

export default CanteenRatingBanner
